// Keyboard rules every roadmap pop-up shares (the initiative pop-up and the
// question pop-up): Escape closes, except from inside a text box where it only
// leaves the box (a half-written answer is not thrown away with the pop-up);
// Tab stays inside the dialog (focus trap). Extra keys (prev / next) are the
// caller's, through `onKey`, and are never fired from a text box either.

import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function inTextBox(e: KeyboardEvent): HTMLElement | null {
  const el = e.target instanceof HTMLElement ? e.target : null;
  return el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.tagName === 'SELECT') ? el : null;
}

export function useModalKeys(
  dialogRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  onKey?: (key: string) => boolean | void,
): void {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const box = inTextBox(e);
        if (box) { box.blur(); return; }
        onClose();
        return;
      }
      if (e.key !== 'Tab') {
        if (onKey && !inTextBox(e) && onKey(e.key)) e.preventDefault();
        return;
      }
      if (!dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dialogRef, onClose, onKey]);
}
